import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import authService from '../../../services/auth/auth.service';
import config from '../../../utils/config';
import { IToolCall } from '../../../services/aiChat/aiChat.service';

export interface ISocketDonePayload {
  conversation_id: number;
  message_id: number;
  input_tokens: number;
  output_tokens: number;
}

interface UseAiChatSocketReturn {
  isConnected: boolean;
  isStreaming: boolean;
  partialText: string;
  toolCalls: IToolCall[];
  socketError: string | null;
  sendMessage: (conversationId: number | null, message: string) => void;
  cancelMessage: (conversationId: number) => void;
  clearSocketError: () => void;
  onDone: (handler: (payload: ISocketDonePayload) => void) => void;
  onError: (handler: (message: string) => void) => void;
}

export const useAiChatSocket = (): UseAiChatSocketReturn => {
  const { inProgress } = useMsal();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [partialText, setPartialText] = useState('');
  const [toolCalls, setToolCalls] = useState<IToolCall[]>([]);
  const [socketError, setSocketError] = useState<string | null>(null);

  const doneHandlerRef = useRef<((payload: ISocketDonePayload) => void) | null>(null);
  const errorHandlerRef = useRef<((message: string) => void) | null>(null);
  // Limits Unauthorized retries to one fresh-token attempt per mount
  const authRetryRef = useRef(0);

  useEffect(() => {
    // Wait for MSAL to finish before connecting — getAuthToken() may return
    // a stale/null value while inProgress !== None.
    if (inProgress !== InteractionStatus.None) return;
    if (socketRef.current) return;

    let mounted = true;

    const connect = async (forceRefresh = false) => {
      const token = await authService.getAuthToken(forceRefresh);
      if (!mounted || !token) return;

      const socket = io(`${config.baseApi}/ai-chat`, {
        extraHeaders: { Authorization: `Bearer ${token}` },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        if (mounted) {
          authRetryRef.current = 0; // reset on successful connect
          setIsConnected(true);
        }
      });

      socket.on('disconnect', () => {
        if (mounted) setIsConnected(false);
      });

      socket.on('connect_error', (err) => {
        if (mounted) setSocketError(`Unable to connect to AI Chat service: ${err.message}`);
      });

      socket.on('ai-chat:stream', ({ chunk }: { chunk: string }) => {
        if (mounted) setPartialText((prev) => prev + chunk);
      });

      socket.on(
        'ai-chat:tool-call',
        ({ sql, description }: { sql: string; description: string }) => {
          if (mounted) setToolCalls((prev) => [...prev, { sql, description }]);
        }
      );

      socket.on(
        'ai-chat:tool-result',
        ({ row_count, capped }: { row_count: number; capped: boolean }) => {
          if (mounted) {
            setToolCalls((prev) => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[updated.length - 1] = { ...updated[updated.length - 1], row_count, capped };
              }
              return updated;
            });
          }
        }
      );

      socket.on('ai-chat:done', (payload: ISocketDonePayload) => {
        if (mounted) {
          setIsStreaming(false);
          if (doneHandlerRef.current) doneHandlerRef.current(payload);
        }
      });

      socket.on('ai-chat:error', ({ message }: { message: string }) => {
        if (!mounted) return;

        // If the backend rejected the connection as Unauthorized, the token
        // acquired at connect time may have been stale (common on direct
        // navigation before the MSAL cache is warm). Retry once with a
        // freshly-acquired token before showing the error to the user.
        if (message === 'Unauthorized' && authRetryRef.current < 1) {
          authRetryRef.current += 1;
          socket.disconnect();
          socketRef.current = null;
          setTimeout(() => { if (mounted) connect(true); }, 300);
          return;
        }

        setIsStreaming(false);
        setSocketError(message);
        if (errorHandlerRef.current) errorHandlerRef.current(message);
      });
    };

    connect();

    return () => {
      mounted = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [inProgress]);

  const sendMessage = useCallback((conversationId: number | null, message: string) => {
    setIsStreaming(true);
    setPartialText('');
    setToolCalls([]);
    setSocketError(null);
    socketRef.current?.emit('ai-chat:message', {
      conversation_id: conversationId,
      message,
    });
  }, []);

  const cancelMessage = useCallback((conversationId: number) => {
    socketRef.current?.emit('ai-chat:cancel', { conversation_id: conversationId });
    setIsStreaming(false);
  }, []);

  const clearSocketError = useCallback(() => setSocketError(null), []);

  const onDone = useCallback((handler: (payload: ISocketDonePayload) => void) => {
    doneHandlerRef.current = handler;
  }, []);

  const onError = useCallback((handler: (message: string) => void) => {
    errorHandlerRef.current = handler;
  }, []);

  return {
    isConnected,
    isStreaming,
    partialText,
    toolCalls,
    socketError,
    sendMessage,
    cancelMessage,
    clearSocketError,
    onDone,
    onError,
  };
};
